import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { requestAPI } from '../../data/api'
import Loading from '../../generic/Loading'

class ImagesOverview extends Component {
	constructor(props) {
		super(props)
		// State is now managed by parent component
	}

	async componentDidMount() {
		await this.loadAvailableClients()
	}

	loadAvailableClients = async () => {
		this.props.updateSharedState({ loadingClients: true })
		try {
			// Get all main clients from /hubs/main endpoint
			const response = await requestAPI('/hubs/main', 'GET')

			// Process the response - expecting an array of hub objects
			const availableClients = Array.isArray(response)
				? response.map((item) => ({
						client_id: item.client_id,
						client_name: item.name || `Client ${item.client_id}`,
						client_short_name: item.short_name || '',
					}))
				: []

			this.props.updateSharedState({
				availableClients,
				loadingClients: false,
			})
		} catch (error) {
			console.error('Failed to load available clients:', error)
			this.props.updateSharedState({
				availableClients: [],
				loadingClients: false,
			})
		}
	}

	loadImages = async () => {
		const {
			selectedClientId,
			startDate,
			endDate,
			photographerName,
			clientImageId,
			supplier,
			page,
			pageSize,
		} = this.props.sharedState

		// Don't make API call if no client is selected
		if (!selectedClientId || selectedClientId === '') {
			alert('Please select a client first')
			return
		}

		this.props.updateSharedState({ loading: true })
		try {
			// Build query parameters
			const params = new URLSearchParams()
			params.append('clientId', selectedClientId)
			params.append('page', page)
			params.append('pageSize', pageSize)

			if (startDate) params.append('startDate', startDate)
			if (endDate) params.append('endDate', endDate)
			if (photographerName) params.append('photographerName', photographerName)
			if (clientImageId) params.append('clientImageId', clientImageId)
			if (supplier) params.append('supplier', supplier)

			// Fetch images and count in parallel
			const [images, countResponse] = await Promise.all([
				requestAPI(`/images/filtered?${params.toString()}`, 'GET'),
				requestAPI(`/images/filtered/count?${params.toString()}`, 'GET'),
			])

			this.props.updateSharedState({
				images: Array.isArray(images) ? images : [],
				totalImages: (countResponse && countResponse.count) || 0,
				loading: false,
			})
		} catch (error) {
			console.error('Failed to load images:', error)
			alert('Failed to load images. Please try again.')
			this.props.updateSharedState({
				images: [],
				loading: false,
			})
		}
	}

	handleClientChange = (e) => {
		this.props.updateSharedState({ selectedClientId: e.target.value, page: 1 })
	}

	handleFilterChange = (field) => (e) => {
		this.props.updateSharedState({ [field]: e.target.value, page: 1 })
	}

	handlePageSizeChange = (e) => {
		const value = parseInt(e.target.value, 10)
		if (value > 0) {
			this.props.updateSharedState({ pageSize: value, page: 1 })
		}
	}

	handleSearch = () => {
		this.loadImages()
	}

	handleNextPage = () => {
		const { page } = this.props.sharedState
		this.props.updateSharedState({ page: page + 1 })
		setTimeout(() => this.loadImages(), 0)
	}

	handlePrevPage = () => {
		const { page } = this.props.sharedState
		if (page > 1) {
			this.props.updateSharedState({ page: page - 1 })
			setTimeout(() => this.loadImages(), 0)
		}
	}

	handlePageChange = (e) => {
		const value = parseInt(e.target.value, 10)
		if (value > 0) {
			this.props.updateSharedState({ page: value })
		}
	}

	handleSort = (column) => {
		const { sortColumn, sortDirection, images } = this.props.sharedState

		// Toggle direction if clicking the same column, otherwise default to asc
		let newDirection = 'asc'
		if (sortColumn === column) {
			newDirection = sortDirection === 'asc' ? 'desc' : 'asc'
		}

		// Sort the data
		const sortedData = [...images].sort((a, b) => {
			let aVal = a[column]
			let bVal = b[column]

			// Handle string columns
			if (
				column === 'image_id' ||
				column === 'client_image_id' ||
				column === 'supplier' ||
				column === 'photographer_name' ||
				column === 'registration_certificate_number'
			) {
				aVal = (aVal || '').toString().toLowerCase()
				bVal = (bVal || '').toString().toLowerCase()
				return newDirection === 'asc'
					? aVal.localeCompare(bVal)
					: bVal.localeCompare(aVal)
			}

			// Handle date column
			if (column === 'created_at') {
				aVal = aVal ? new Date(aVal).getTime() : 0
				bVal = bVal ? new Date(bVal).getTime() : 0
				return newDirection === 'asc' ? aVal - bVal : bVal - aVal
			}

			return 0
		})

		this.props.updateSharedState({
			images: sortedData,
			sortColumn: column,
			sortDirection: newDirection,
		})
	}

	handleExportCSV = () => {
		const { images } = this.props.sharedState

		if (!images || images.length === 0) {
			alert('No images to export')
			return
		}

		// Define CSV headers
		const headers = [
			'Client Image ID',
			'Supplier',
			'Photographer Name',
			'Registration Number',
			'Created At',
		]

		// Convert images data to CSV rows
		const rows = images.map((image) => [
			image.client_image_id || '',
			image.supplier || '',
			image.photographer_name || '',
			image.registration_certificate_number || '',
			image.created_at ? new Date(image.created_at).toLocaleDateString() : '',
		])

		// Escape CSV values (handle commas and quotes)
		const escapeCSV = (value) => {
			const stringValue = String(value)
			if (
				stringValue.includes(',') ||
				stringValue.includes('"') ||
				stringValue.includes('\n')
			) {
				return `"${stringValue.replace(/"/g, '""')}"`
			}
			return stringValue
		}

		// Build CSV content
		const csvContent = [
			headers.map(escapeCSV).join(','),
			...rows.map((row) => row.map(escapeCSV).join(',')),
		].join('\n')

		// Create blob and download
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
		const link = document.createElement('a')
		const url = URL.createObjectURL(blob)

		// Generate filename with timestamp
		const timestamp = new Date().toISOString().split('T')[0]
		const filename = `images_export_${timestamp}.csv`

		link.setAttribute('href', url)
		link.setAttribute('download', filename)
		link.style.visibility = 'hidden'
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
	}

	render() {
		const {
			images,
			loading,
			availableClients,
			selectedClientId,
			loadingClients,
			startDate,
			endDate,
			photographerName,
			clientImageId,
			supplier,
			page,
			pageSize,
			totalImages,
			sortColumn,
			sortDirection,
		} = this.props.sharedState

		const getSortIndicator = (column) => {
			if (sortColumn !== column) return null
			return sortDirection === 'asc' ? ' ↑' : ' ↓'
		}

		const totalPages = Math.ceil(totalImages / pageSize)
		const startIndex = (page - 1) * pageSize + 1
		const endIndex = Math.min(page * pageSize, totalImages)

		return (
			<div className='images-overview'>
				{/* Filters */}
				<div className='images-filters'>
					<div className='filter-group'>
						<label htmlFor='client-select'>Client</label>
						{loadingClients ? (
							<div style={{ padding: '8px' }}>Loading clients...</div>
						) : (
							<select
								id='client-select'
								value={selectedClientId}
								onChange={this.handleClientChange}
								className='filter-input'
							>
								<option value=''>-- Select a client --</option>
								{availableClients.map((client) => (
									<option key={client.client_id} value={client.client_id}>
										{client.client_name} - {client.client_short_name}
									</option>
								))}
							</select>
						)}
					</div>

					<div className='filter-group'>
						<label htmlFor='start-date'>Start Date (On or After)</label>
						<input
							id='start-date'
							type='date'
							value={startDate}
							onChange={this.handleFilterChange('startDate')}
							className='filter-input'
						/>
					</div>

					<div className='filter-group'>
						<label htmlFor='end-date'>End Date (On or Before)</label>
						<input
							id='end-date'
							type='date'
							value={endDate}
							onChange={this.handleFilterChange('endDate')}
							className='filter-input'
						/>
					</div>

					<div className='filter-group'>
						<label htmlFor='supplier'>Supplier</label>
						<input
							id='supplier'
							type='text'
							value={supplier}
							onChange={this.handleFilterChange('supplier')}
							placeholder='Enter supplier name'
							className='filter-input'
						/>
					</div>

					<div className='filter-group'>
						<label htmlFor='photographer-name'>Photographer</label>
						<input
							id='photographer-name'
							type='text'
							value={photographerName}
							onChange={this.handleFilterChange('photographerName')}
							placeholder='Enter photographer name'
							className='filter-input'
						/>
					</div>

					<div className='filter-group'>
						<label htmlFor='client-image-id'>Client Image ID</label>
						<input
							id='client-image-id'
							type='text'
							value={clientImageId}
							onChange={this.handleFilterChange('clientImageId')}
							placeholder='Enter client image ID'
							className='filter-input'
						/>
					</div>

					<div className='filter-group'>
						<label htmlFor='page-size'>Images per page</label>
						<input
							id='page-size'
							type='number'
							value={pageSize}
							onChange={this.handlePageSizeChange}
							min='1'
							className='filter-input'
						/>
					</div>

					<div className='filter-actions'>
						<button onClick={this.handleSearch} className='search-button'>
							Search
						</button>
					</div>
				</div>

				{/* Results Info */}
				{totalImages > 0 && (
					<div className='results-info'>
						<span>
							Showing {startIndex} - {endIndex} of {totalImages} images
						</span>
						<button
							onClick={this.handleExportCSV}
							className='export-button'
							disabled={!images || images.length === 0}
							title={
								images && images.length > 0
									? 'Export current results to CSV'
									: 'No data to export'
							}
						>
							Export as CSV
						</button>
					</div>
				)}

				{/* Loading State */}
				{loading && (
					<div className='loading-container'>
						<Loading />
					</div>
				)}

				{/* Table */}
				{!loading && images.length > 0 && (
					<div className='images-table-container'>
						<table className='images-table'>
							<thead>
								<tr>
									<th
										onClick={() => this.handleSort('client_image_id')}
										style={{ cursor: 'pointer' }}
									>
										Client Image ID{getSortIndicator('client_image_id')}
									</th>
									<th
										onClick={() => this.handleSort('supplier')}
										style={{ cursor: 'pointer' }}
									>
										Supplier{getSortIndicator('supplier')}
									</th>
									<th
										onClick={() => this.handleSort('photographer_name')}
										style={{ cursor: 'pointer' }}
									>
										Photographer Name{getSortIndicator('photographer_name')}
									</th>
									<th
										onClick={() =>
											this.handleSort('registration_certificate_number')
										}
										style={{ cursor: 'pointer' }}
									>
										Registration Number
										{getSortIndicator('registration_certificate_number')}
									</th>
									<th
										onClick={() => this.handleSort('created_at')}
										style={{ cursor: 'pointer' }}
									>
										Created At{getSortIndicator('created_at')}
									</th>
								</tr>
							</thead>
							<tbody>
								{images.map((image, index) => (
									<tr key={image.image_id || index}>
										<td>{image.client_image_id || 'N/A'}</td>
										<td>{image.supplier || 'N/A'}</td>
										<td>{image.photographer_name || 'N/A'}</td>
										<td>{image.registration_certificate_number || 'N/A'}</td>
										<td>
											{image.created_at
												? new Date(image.created_at).toLocaleDateString()
												: 'N/A'}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}

				{/* Empty State */}
				{!loading && images.length === 0 && selectedClientId && (
					<div className='empty-state'>
						<p>No images found with the current filters.</p>
					</div>
				)}

				{/* Pagination */}
				{!loading && images.length > 0 && (
					<div className='pagination'>
						<button
							onClick={this.handlePrevPage}
							disabled={page === 1}
							className='pagination-button'
						>
							Previous
						</button>
						<span className='pagination-info'>
							Page{' '}
							<input
								type='number'
								value={page}
								onChange={this.handlePageChange}
								onBlur={() => this.loadImages()}
								className='page-input'
								min='1'
								max={totalPages}
							/>{' '}
							of {totalPages}
						</span>
						<button
							onClick={this.handleNextPage}
							disabled={page >= totalPages}
							className='pagination-button'
						>
							Next
						</button>
					</div>
				)}
			</div>
		)
	}
}

ImagesOverview.propTypes = {
	client_ids: PropTypes.array.isRequired,
	sharedState: PropTypes.object.isRequired,
	updateSharedState: PropTypes.func.isRequired,
}

ImagesOverview.contextTypes = {
	gettext: PropTypes.func.isRequired,
}

export default ImagesOverview
